import {fabric} from 'fabric';
import ResizeObserver from 'resize-observer-polyfill';
import EraserBrush from './eraser-brush';


function getDefaultOption() {
  return {
    brushWidth: 50,
    brushColor: '#ff0000',
    objectSelectable: false
  };
}


export default class Whiteboard {
  constructor(mountElm, option) {
    let opt = Object.assign({}, getDefaultOption(), option);
    this.opt = opt;

    this._mountElm = mountElm;
    let canvasElm = document.createElement('canvas');
    this._mountElm.appendChild(canvasElm);
    this._canvas = new fabric.Canvas(canvasElm, {
      isDrawingMode: true,
      // disable group selection
      selection: false
    });
    // override rendering function to make background image always rendered on the bottom layer.
    this._canvas.renderCanvas = renderCanvas;

    this._pencilBrush = new fabric.PencilBrush(this._canvas);
    this._eraserBrush = new EraserBrush(this._canvas);
    this._canvas.freeDrawingBrush = this._pencilBrush;

    this.brushColor = opt.brushColor;
    this.brushWidth = opt.brushWidth;

    // NOTE:
    // The pollyfill for ResizeObserver is not work very well on Chromium and FireFox
    this._mountElmRO = new ResizeObserver(this._onResize.bind(this));
    this._mountElmRO.observe(mountElm);
    // this._onResizeListener = this._onResize.bind(this);
    // window.addEventListener('resize', this._onResizeListener);

    this._eventHandlers = [];

    this._boards = {};
    this._undoStack = [];
    this._currentBoardId = null;
    this._currentPageIndex = 0;
    this._imageScaledSize = {};

    this._registerCanvasEvents();
  }

  addBoard(id, imageUrl) {
    if (!id) {
      throw new Error('board id is required');
    }
    /**
     * {
     *    image: String | Blob | Image,
     *    showImage: true,
     *    pages: [
     *      {objects: Array, showImage: true}
     *    ]
     * }
     */
    this._boards[id] = {
      image: imageUrl,
      showImage: true,
      pages: []
    };
  }

  async switchBoard(id) {
    if (!this._boards[id]) {
      throw new Error('failed to switch board: board not found. id:' + id);
    }

    await this.switchPage(0, id);
    return Promise.resolve(this._boards[id]);
  }

  removeBoard(id) {
    if (!this._boards[id]) {
      throw new Error('failed to remove board: board not found. id:' + id);
    }

    delete this._boards[id];
  }

  async switchPage(index, targetBoardId) {
    this._saveBoard();

    this._currentPageIndex = index;
    if (targetBoardId !== undefined) {
      this._currentBoardId = targetBoardId;
    }

    this._removeAllObjects();

    let board = this._boards[this._currentBoardId];
    let page = board.pages[this._currentPageIndex];
    // if page does not exist, then create it with default values.
    if (!page) {
      page = {objects: [], showImage: true};
      board.pages[this._currentPageIndex] = page;
    }

    this._canvas.add(...page.objects);

    await this.setBackgroundImageVisible(page.showImage);

    this._animateSwitch();
    return page;
  }

  async removePage(index) {
    let board = this._boards[this._currentBoardId];
    board.page.splice(index, 1);
    await this.switchPage(0);
  }

  loadImage(src) {
    return new Promise((resolve, reject) => {
      fabric.Image.fromURL(src, img => {
        this._image = img;
        // 'destination-over' mode will make image rendered on the bottom layer.
        this._image.globalCompositeOperation = 'destination-over';

        // image rotate angle
        this._image._rotateAngle = 0;
        let canvas = this._canvas;
        // OverlayImage is the last object to be rendered in canvas. (see 'renderCanvas' function)
        // When the globalCompositeOperation mode of OverlayImage is 'destination-over',
        // it will be rendered at the bottom layer and other objects will be overlapping on it,
        // that's the whiteboard background image we want.
        canvas.setOverlayImage(img, canvas.renderAll.bind(canvas), {
          left: 0,
          top: 0,
          // setup object's origin coordinate
          // the origin coordinate is the base point of object for outside to access.
          originX: 'center',
          originY: 'center'
        });

        this._resizeImage();
        resolve(img);
      });
    });
  }

  clearImage() {
    this._image = null;
    this._canvas.setOverlayImage(null);
  }

  getBlob() {
    return new Promise((resolve, reject) => {
      try {
        this.getLowerCanvas().toBlob(blob => resolve(blob));
      } catch (e) {
        reject(e);
      }
    });
  }

  get boards() {
    return this._boards;
  }

  set freeDrawing(v) {
    this._canvas.isDrawingMode = v || false;
  }

  get freeDrawing() {
    return this._canvas.isDrawingMode;
  }

  set cursor(v) {
    this._canvas.defaultCursor = v;
  }

  get cursor() {
    return this._canvas.defaultCursor;
  }

  set eraser(value) {
    this._canvas._isEraser = Boolean(value);
    if (this._canvas._isEraser) {
      this._canvas.freeDrawingBrush = this._eraserBrush;
    } else {
      this._canvas.freeDrawingBrush = this._pencilBrush;
    }
    this._render();
  }

  get eraser() {
    return this._canvas._isEraser || false;
  }

  set brushColor(value) {
    this._pencilBrush.color = value;
  }

  get brushColor() {
    return this._pencilBrush.color;
  }

  set brushWidth(value) {
    this._pencilBrush.width = value;
    this._eraserBrush.width = value;
  }

  get brushWidth() {
    return this._pencilBrush.width;
  }

  async setBackgroundImageVisible(visible) {
    let board = this._boards[this._currentBoardId];
    let page = board.pages[this._currentPageIndex];
    page.showImage = !!visible;

    if (board.image && page.showImage) {
      await this.loadImage(board.image);
    } else {
      this.clearImage();
    }

    if (this._image) {
      this._image.visible = page.showImage;
    }

    this._render();
  }

  get isBackgroundImageVisible() {
    let board = this._boards[this._currentBoardId];
    let page = board.pages[this._currentPageIndex];
    return page.showImage;
  }

  undo() {
    let obj = this._canvas.getObjects().pop();
    if (!obj) {
      return;
    }
    this._undoStack.push(obj);
    this._canvas.remove(obj);
    this._render();
    return this._undoStack.length;
  }

  redo() {
    let obj = this._undoStack.pop();
    if (!obj) {
      return;
    }

    if (obj instanceof Array) {
      obj.forEach(o => this._canvas.add(o));
    } else {
      this._canvas.add(obj);
    }
    this._render();
    return this._undoStack.length;
  }

  rotateBackgroundImage() {
    if (!this._image) {
      return;
    }
    let angle = this._image._rotateAngle || 0;
    angle = (angle - 90) % 360;
    this._image._rotateAngle = angle;
    this._canvas.overlayImage.rotate(angle);
    this._render();
  }

  setTransform(scale, translateX, translateY, isEnd) {
    translateX += this._currentCanvasWidth/2;
    translateY += this._currentCanvasHeight/2;
    this._canvas.setViewportTransform([scale, 0, 0, scale, translateX, translateY]);
  }

  clearObjects() {
    let objs = this._canvas.getObjects();
    if (!objs.length) {
      return;
    }
    this._undoStack.push(objs);
    objs.forEach(o => this._canvas.remove(o));
    this._render();
    return this._undoStack.length;
  }

  clear() {
    this._image = null;
    this._canvas.clear();
  }

  destroy() {
    this._mountElmRO.disconnect();
    this._mountElmRO.unobserve(this._mountElm);
    // window.removeEventListener('resize', this._onResizeListener);
    this._canvas.clear();
    this._canvas.dispose();
  }

  getCanvasContainer() {
    return this._canvas.wrapperEl;
  }

  getUpperCanvas() {
    return this._canvas.upperCanvasEl;
  }

  getLowerCanvas() {
    return this._canvas.lowerCanvasEl;
  }

  on(eventName, callback) {
    this._eventHandlers.push({
      name: eventName,
      handler: callback
    });
  }

  refresh() {
    this._render();
  }

  _animateSwitch() {
    let opacity = 1;
    let elm = this._canvas.upperCanvasEl;

    const animateFrame = () => {
      opacity -= 0.1;
      elm.style.backgroundColor = `rgba(150,150,150,${opacity})`;
      if (opacity <= 0) {
        elm.style.backgroundColor = 'transparent';
        return;
      }
      requestAnimationFrame(animateFrame);
    };
    requestAnimationFrame(animateFrame);
  }

  _registerCanvasEvents() {
    let canvas = this._canvas;

    canvas.on('after:render', event => {
      let handlers = this._eventHandlers.filter(e => {
        return e.name === 'afterRender';
      });
      handlers.forEach(h => h.handler(event));
    });

    canvas.on('mouse:down', event => {
      if (!event.target) {
        this._mouseStart = event;
      } else {
        this._mouseStart = null;
      }
    });

    canvas.on('path:created', event => {
      this._undoStack = [];
      let obj = this._canvas.getObjects()[this._canvas.getObjects().length-1];
      obj.selectable = this.opt.objectSelectable;
      let originLeft = obj.left;
      let originRight = obj.top;
      obj._pathInfo = {
        originLeft: originLeft,
        originRight: originRight,
        originImageWidth: this._imageScaledSize.width,
        originImageHeight: this._imageScaledSize.height,
        originCanvasWidth: this._currentCanvasWidth,
        originCanvasHeight: this._currentCanvasHeight
      };
    });
  }

  _saveBoard() {
    if (this._currentBoardId === null || this._currentBoardId === undefined) {
      return;
    }

    let board = this._boards[this._currentBoardId];
    if (!board) {
      return;
    }

    let page = board.pages[this._currentPageIndex];
    if (!page) {
      return;
    }
    page.objects = this._canvas.getObjects();
  }

  _removeAllObjects() {
    // clear all objects
    this._canvas.getObjects().forEach(o => this._canvas.remove(o));
    // clear upper canvas
    this._canvas.clearContext(this._canvas.contextTop);
  }

  _onResize(entries, observer) {
    // see polyfill github: https://github.com/que-etc/resize-observer-polyfill
    /*
    entry.contentRect: {
      x: 16  // start x
      y: 16  // start y

      top: 16 // start y
      left: 16 // start x
      bottom: 847.636  // end y
      right: 3007.98 // end x

      width: 2991.98
      height: 831.636
    }
    */

    clearTimeout(this._resizeTimeout);
    this._resizeTimeout = setTimeout(() => {
      let containerSize = getElementInnerSize(this._mountElm);
      this._canvas.setWidth(containerSize.width);
      this._canvas.setHeight(containerSize.height);
      // Calculates canvas element offset relative to the document.
      // This method is also attached as "resize" event handler of window
      this._canvas.calcOffset();

      this._currentCanvasWidth = containerSize.width;
      this._currentCanvasHeight = containerSize.height;
      let v = this._canvas.viewportTransform;
      this._canvas.setViewportTransform([v[0], 0, 0, v[3], containerSize.width/2, containerSize.height/2]);
      this._resizeImage();
      this._scaleObjects();
      this._render();
    }, 50);
  }

  _resizeImage() {
    let image = this._image;
    if (!image) {
      return;
    }
    let canvas = this._canvas;

    let rotateAngle = Math.abs(image._rotateAngle);
    let imageWidth = image.width;
    let imageHeight = image.height;
    if (rotateAngle === 90 || rotateAngle === 270) {
      imageWidth = image.height;
      imageHeight = image.width;
    }
    let scaledSize = getImageScaleInfo(imageWidth, imageHeight, canvas.width, canvas.height);
    this._image.scale(scaledSize.scale);
    this._imageScaledSize = scaledSize;
  }

  _scaleObjects() {
    // do nothing if there is no background image.(empty board)
    if (!this._image) {
      return;
    }
    this._canvas.getObjects().forEach(obj => {
      let scaleX = this._imageScaledSize.width / obj._pathInfo.originImageWidth;
      let scaleY = this._imageScaledSize.height / obj._pathInfo.originImageHeight;
      obj.set({
        left: obj._pathInfo.originLeft * scaleX,
        top: obj._pathInfo.originRight * scaleY,
        // width: obj._pathInfo.absWidth * this._currentCanvasWidth,
        // height: obj._pathInfo.absHeight * this._currentCanvasHeight,
        // strokeWidth: obj._pathInfo.strokeWidth * scale || 1,
        // strokeMiterLimit: obj._pathInfo.strokeMiterLimit * scale || 1
        // originX: 'center',
        // originY: 'center',
        scaleX: scaleX,
        scaleY: scaleY
      });
      obj.setCoords();
    });
  }

  _render() {
    this._canvas.renderAll();
  }
}

// Override funciton for fabric's canvas object.
// The purpose of override is to change the rendering order between background image and object.
// Make image always rendered after object with composite mode 'destination-over' so that it will
// always rendered on the bottom layer.
const renderCanvas = function(ctx, objects) {
  let v = this.viewportTransform;
  let path = this.clipPath;
  this.cancelRequestedRender();
  this.calcViewportBoundaries();
  this.clearContext(ctx);
  this.fire('before:render', {ctx: ctx});
  if (this.clipTo) {
    fabric.util.clipContext(this, ctx);
  }
  this._renderBackground(ctx);
  ctx.save();

  // apply viewport transform once for all rendering process
  ctx.transform(v[0], v[1], v[2], v[3], v[4], v[5]);

  // if it is eraser mode, render objects on top canvas for erasing,
  // else render on low canvas.
  if (this._isEraser) {
    this.contextTop.save();
    this.clearContext(this.contextTop);
    this.contextTop.transform(v[0], v[1], v[2], v[3], v[4], v[5]);
    this._renderObjects(this.contextTop, objects);
    this.contextTop.restore();
  } else {
    // clear top canvas because in eraser mode all objects will render on it.
    this.clearContext(this.contextTop);
    this._renderObjects(ctx, objects);
  }

  ctx.restore();
  if (!this.controlsAboveOverlay && this.interactive) {
    this.drawControls(ctx);
  }
  if (this.clipTo) {
    ctx.restore();
  }
  if (path) {
    path.canvas = this;
    // needed to setup a couple of variables
    path.shouldCache();
    path._transformDone = true;
    path.renderCache({forClipping: true});
    this.drawClipPathOnCanvas(ctx);
  }
  this._renderOverlay(ctx);
  if (this.controlsAboveOverlay && this.interactive) {
    this.drawControls(ctx);
  }
  this.fire('after:render', {ctx: ctx});
};

const getElementInnerSize = el => {
  let rect = el.getBoundingClientRect();
  let paddingLeft = window.getComputedStyle(el, null).getPropertyValue('padding-left');
  let paddingRight = window.getComputedStyle(el, null).getPropertyValue('padding-right');
  let paddingTop = window.getComputedStyle(el, null).getPropertyValue('padding-top');
  let paddingBottom = window.getComputedStyle(el, null).getPropertyValue('padding-bottom');

  let width = rect.width - parseFloat(paddingLeft) - parseFloat(paddingRight);
  let height = rect.height - parseFloat(paddingTop) - parseFloat(paddingBottom);

  return {width, height};
};

const getImageScaleInfo = (imgWidth, imgHeight, maxWidth, maxHeight) => {
  let wRatio = maxWidth / imgWidth;
  let hRatio = maxHeight / imgHeight;

  let scale = 1;
  if (imgWidth > maxWidth && wRatio < hRatio) {
    scale = wRatio;
  } else if (imgHeight > maxHeight && hRatio < wRatio) {
    scale = hRatio;
  }
  let width = Number((imgWidth * scale).toFixed(0));
  let height = Number((imgHeight * scale).toFixed(0));

  return {width, height, scale};
};